"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseSchematic = parseSchematic;
exports.parseSheet = parseSheet;
exports.getComponentName = getComponentName;
exports.parseComponentSignals = parseComponentSignals;
exports.parseComponentDefinition = parseComponentDefinition;
exports.parseSchematicXml = parseSchematicXml;
exports.getSymbol = getSymbol;
exports.loadSymbol = loadSymbol;
const xml_js_1 = require("xml-js");
const common_1 = require("./common");
const Area_1 = require("../Area");
const layers_1 = require("./layers");
const board_1 = require("./board");
const Util_1 = require("../Util");
const strDist = require("fast-levenshtein");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const Common_1 = require("./Types/Common");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
const fonts_1 = require("../Fonts/fonts");
const schematicUtils_1 = require("../schematicUtils");
const ObjMap_1 = require("./ObjMap");
const PROP_NAME = 'NAME';
const PROP_VALUE = 'VALUE';
var PinDirection;
(function (PinDirection) {
    PinDirection["NotConnected"] = "nc";
    PinDirection["Input"] = "in";
    PinDirection["Output"] = "out";
    PinDirection["BidirectionalIO"] = "i/o";
    PinDirection["OpenCollectorOpenDrain"] = "oc";
    PinDirection["HiImpedence"] = "hiz";
    PinDirection["Passive"] = "pas";
    PinDirection["Power"] = "pwr";
    PinDirection["Supply"] = "sup";
})(PinDirection || (PinDirection = {}));
var PinVisibility;
(function (PinVisibility) {
    PinVisibility["Off"] = "off";
    PinVisibility["Pad"] = "pad";
    PinVisibility["Pin"] = "pin";
    PinVisibility["Both"] = "both";
})(PinVisibility || (PinVisibility = {}));
var PinLength;
(function (PinLength) {
    PinLength["Point"] = "point";
    PinLength["Short"] = "short";
    PinLength["Middle"] = "middle";
    PinLength["Long"] = "long";
})(PinLength || (PinLength = {}));
var SignalParametrics;
(function (SignalParametrics) {
    SignalParametrics["HiImpedence"] = "hiimpedence";
})(SignalParametrics || (SignalParametrics = {}));
function parseSchematic(project, schematic, designId) {
    if (project.type !== 'eagle') {
        throw new Error('Cannot parse eagle schematic to non-eagle project');
    }
    const xmlRoot = parseSchematicXml(schematic);
    const schematicXml = xmlRoot.eagle.drawing.schematic;
    if (!project.layerSets[layers_1.EAGLE_SCH_LAYERSET]) {
        project.layerSets[layers_1.EAGLE_SCH_LAYERSET] = readLayers(xmlRoot);
    }
    const grid = (0, common_1.readEagleGrid)(xmlRoot.eagle.drawing.grid, 'schematic');
    return (0, Common_1.toArray)(schematicXml.sheets.sheet).map((sheet, index) => parseSheet(project, schematicXml, grid, designId, sheet, index));
}
function parseSheet(project, schematicXml, grid, sourceFile, sheet, sheetIndex) {
    const model = {
        modelType: 'schematic',
        designId: `${sourceFile} sheet ${sheetIndex}`,
        sourceFile,
        sheetIndex,
        edges_bbox: {
            minx: Number.POSITIVE_INFINITY,
            miny: Number.POSITIVE_INFINITY,
            maxx: Number.NEGATIVE_INFINITY,
            maxy: Number.NEGATIVE_INFINITY,
        },
        initial_zoombox: {
            minx: Number.POSITIVE_INFINITY,
            miny: Number.POSITIVE_INFINITY,
            maxx: Number.NEGATIVE_INFINITY,
            maxy: Number.NEGATIVE_INFINITY,
        },
        grid,
        layerSet: layers_1.EAGLE_SCH_LAYERSET,
        drawings: {},
        modules: [],
        nets: {},
        font: {
            default: fonts_1.Font.Newstroke,
        },
        warnings: [],
        ports: {},
    };
    (0, Common_1.toArray)(sheet.plain.text).forEach((t) => {
        let { rot } = (0, common_1.parseRotation)(t._attributes.rot);
        const { justify, mirror } = calculateJustifyAndMirrorForLabels(rot);
        (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getEagleSchLayerByType)(sigma_engine_shared_1.SchLayerType.Label).id, {
            type: 'text',
            value: t._text,
            size: parseFloat(t._attributes.size),
            pos: [parseFloat(t._attributes.x), -parseFloat(t._attributes.y)],
            angle: -rot,
            justify,
            thickness: 0,
            mirror,
            attr: [],
            rejustifyOnRotate: true,
        });
    });
    (0, Common_1.toArray)(sheet.plain.wire).forEach((w) => {
        readWire(model, w);
    });
    const libs = (0, ObjMap_1.toMap)((0, Common_1.toArray)(schematicXml.libraries?.library), (l) => l._attributes.name);
    const libDeviceSets = (0, ObjMap_1.toMap)((0, Common_1.toArray)(schematicXml.libraries?.library), (l) => l._attributes.name, (l) => (0, ObjMap_1.toMap)((0, Common_1.toArray)(l.devicesets?.deviceset), (d) => d._attributes.name));
    for (const part of (0, Common_1.toArray)(schematicXml.parts.part)) {
        const lib = libs[part._attributes.library];
        if (!lib) {
            model.warnings.push(`Could not find library '${part._attributes.library}' for part ${part._attributes.name}`);
            continue;
        }
        for (const instance of (0, Common_1.toArray)(sheet.instances.instance).filter((i) => i._attributes.part === part._attributes.name)) {
            const deviceSet = libDeviceSets[part._attributes.library][part._attributes.deviceset];
            const device = (0, Common_1.toArray)(deviceSet?.devices.device).find((d) => d._attributes.name === part._attributes.device);
            if (!device) {
                model.warnings.push(`Could not find device '${part._attributes.device}' definition for ${part._attributes.name}`);
                continue;
            }
            const rotation = (0, common_1.parseRotation)(instance._attributes.rot);
            const value = part._attributes.value?.trim();
            const module = {
                ref: part._attributes.name,
                footprintRef: `${device._attributes.package}::${instance._attributes.gate}`,
                componentRef: '',
                name: part._attributes.name,
                center: [parseFloat(instance._attributes.x), -parseFloat(instance._attributes.y)],
                layer: 'F',
                drawProperties: {},
                transform: {
                    angle: -rotation.rot,
                    pos: [0, 0],
                    mirrored: rotation.mirrored,
                },
                pinNets: {},
            };
            const component = loadComponent(project, model, lib, libDeviceSets, module, part);
            if (!component) {
                model.warnings.push(`Could not load component for ${module.name}`);
                break;
            }
            if (!component.symbols.find((s) => s.ref === module.footprintRef)) {
                model.warnings.push(`Could not find symbol '${module.footprintRef}' for ${module.name}`);
                break;
            }
            if (lib.description) {
                if (!component.metadata) {
                    component.metadata = {};
                }
                component.metadata['eagle.lib.description'] = lib.description._text;
            }
            if (part._attributes.name) {
                module.drawProperties[PROP_NAME] = locateModuleProperty(module, { value: part._attributes.name });
            }
            if (part._attributes.value) {
                module.drawProperties[PROP_VALUE] = locateModuleProperty(module, { value: part._attributes.value });
            }
            module.componentRef = component.name;
            model.modules.push(module);
            let metaModule = project.modules.find((m) => m.name === module.name);
            if (!metaModule) {
                metaModule = {
                    name: module.name,
                    instances: [],
                    componentRef: component.name,
                };
                project.modules.push(metaModule);
            }
            if (!metaModule.name) {
                metaModule.name = module.name;
            }
            if (value) {
                if (metaModule.value && metaModule.value !== value) {
                    model.warnings.push(`Module '${module.name}' has multiple values: '${metaModule.value}' and '${value}'`);
                }
                metaModule.value = value;
            }
            if (module.metadata && Object.keys(module.metadata).length > 0) {
                if (!metaModule.metadata) {
                    metaModule.metadata = {};
                }
                metaModule.metadata = {
                    ...metaModule.metadata,
                    ...module.metadata,
                };
            }
            metaModule.instances.push({
                ref: module.ref,
                designId: model.designId,
            });
        }
    }
    const schematicNets = (0, Common_1.toArray)(sheet.nets.net);
    schematicNets.forEach((net) => {
        const netDefinition = model.nets[net._attributes.name] || parseNetDefinition(schematicNets, net, model.warnings);
        (0, Common_1.toArray)(net.segment).forEach((segment) => {
            (0, Common_1.toArray)(segment.pinref).forEach((pinRef) => {
                const module = model.modules.find((m) => m.name === pinRef._attributes.part);
                if (!module) {
                    model.warnings.push(`Cannot find module for part: '${pinRef._attributes.part}'`);
                    return;
                }
                const signalId = `${pinRef._attributes.gate}::${pinRef._attributes.pin}`;
                module.pinNets[signalId] = netDefinition.name;
                netDefinition.connections.push({
                    moduleRef: pinRef._attributes.part,
                    pinRef: signalId,
                    moduleName: module.name,
                });
                if (!module.drawProperties['value']) {
                    const component = project.components[module.componentRef];
                    if (component.numPins === 1) {
                        module.drawProperties[PROP_VALUE] = locateModuleProperty(module, {
                            value: Object.values(module.pinNets)[0],
                        });
                    }
                }
            });
            (0, Common_1.toArray)(segment.junction).forEach((j) => {
                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getEagleSchLayerByType)(sigma_engine_shared_1.SchLayerType.Junction).id, {
                    type: 'junction',
                    position: [parseFloat(j._attributes.x), -parseFloat(j._attributes.y)],
                    net: netDefinition.name,
                });
            });
            (0, Common_1.toArray)(segment.wire).forEach((w) => {
                readWire(model, w, net);
            });
            (0, Common_1.toArray)(segment.label).forEach((w) => {
                const { rot } = (0, common_1.parseRotation)(w._attributes.rot);
                const { justify, mirror } = calculateJustifyAndMirrorForLabels(rot);
                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getEagleSchLayerByType)(sigma_engine_shared_1.SchLayerType.Label).id, {
                    type: 'text',
                    value: w._attributes.name || net._attributes.name,
                    attr: [],
                    justify,
                    pos: [parseFloat(w._attributes.x), -parseFloat(w._attributes.y)],
                    size: parseFloat(w._attributes.size),
                    thickness: 1,
                    angle: -rot,
                    mirror,
                    rejustifyOnRotate: true,
                    net: net._attributes.name,
                });
            });
        });
        if (netDefinition.isGlobal) {
            if (!project.globalNets[net._attributes.name]) {
                project.globalNets[net._attributes.name] = {
                    name: net._attributes.name,
                };
            }
        }
        model.nets[net._attributes.name] = netDefinition;
    });
    const bbox = new Area_1.default();
    bbox.extendFromSchModel(project, model, project.layerSets[layers_1.EAGLE_SCH_LAYERSET], model.warnings);
    model.edges_bbox = {
        minx: bbox.xMin,
        miny: bbox.yMin,
        maxx: bbox.xMax,
        maxy: bbox.yMax,
    };
    const visibleBbox = new Area_1.default();
    visibleBbox.extendFromSchModel(project, model, project.layerSets[layers_1.EAGLE_SCH_LAYERSET], model.warnings, false);
    model.initial_zoombox = {
        minx: visibleBbox.xMin,
        miny: visibleBbox.yMin,
        maxx: visibleBbox.xMax,
        maxy: visibleBbox.yMax,
    };
    (0, Util_1.dedupeWarnings)(model);
    return model;
}
function parseNetDefinition(nets, net, warnings) {
    const netInfo = {
        name: net._attributes.name,
        connections: [],
        metadata: {
            netclass: net._attributes.class,
        },
        isGlobal: false,
    };
    if (net._attributes.name?.toLowerCase().endsWith('_p') || net._attributes.name?.toLowerCase().endsWith('_n')) {
        const pairName = net._attributes.name.slice(0, -2);
        netInfo.diffPair = {
            pairName,
            positiveId: net._attributes.name?.toLowerCase().endsWith('_p') ?
                net._attributes.name
                : nets.find((n) => n._attributes.name.toLowerCase() === (pairName + '_P').toLowerCase())?._attributes
                    .name,
            negativeId: net._attributes.name?.toLowerCase().endsWith('_n') ?
                net._attributes.name
                : nets.find((n) => n._attributes.name.toLowerCase() === (pairName + '_N').toLowerCase())?._attributes
                    .name,
        };
        if (!netInfo.diffPair.positiveId) {
            warnings.push(`Could not find positive net for differential pair '${netInfo.diffPair.pairName}'`);
            netInfo.diffPair.positiveId = pairName + '_P';
        }
        if (!netInfo.diffPair.negativeId) {
            warnings.push(`Could not find negative net for differential pair '${netInfo.diffPair.pairName}'`);
            netInfo.diffPair.negativeId = pairName + '_N';
        }
    }
    return netInfo;
}
function readWire(model, w, net) {
    const parsed = parseWire(w, net);
    (0, schematicUtils_1.addSchematicDrawing)(model, parsed.layer, parsed.shape);
}
function parseWire(w, net) {
    return {
        layer: parseInt(w._attributes.layer),
        shape: {
            type: 'track',
            start: [parseFloat(w._attributes.x1), -parseFloat(w._attributes.y1)],
            end: [parseFloat(w._attributes.x2), -parseFloat(w._attributes.y2)],
            width: 0,
            net: net?._attributes.name,
        },
    };
}
function readLayers(xmlRoot) {
    return (0, Common_1.toArray)(xmlRoot.eagle.drawing.layers.layer)
        .map((l) => {
        if (!(0, layers_1.isLayerActive)(l)) {
            return;
        }
        return readLayer(l);
    })
        .filter((l) => !!l);
}
function readLayer(layer) {
    const layerId = parseInt(layer._attributes.number);
    const parsed = (0, layers_1.parseSchLayer)(layerId, layer._attributes.name);
    if ((0, layers_1.isLayerVisible)(layer)) {
        parsed.defaultVisible = true;
    }
    return parsed;
}
function loadComponent(project, model, lib, libDeviceSets, module, part) {
    const deviceSet = libDeviceSets[part._attributes.library][part._attributes.deviceset];
    const device = (0, Common_1.toArray)(deviceSet.devices.device).find((d) => d._attributes.name === part._attributes.device);
    if (!device && part._attributes.device.length > 0) {
        model.warnings.push(`Could not find device '${part._attributes.device}' definition for ${module.footprintRef}`);
        return;
    }
    let footprint;
    if (device && device._attributes.package) {
        const devicePackage = (0, Common_1.toArray)(lib.packages.package).find((p) => p._attributes.name === device._attributes.package);
        if (!devicePackage) {
            model.warnings.push(`Could not find device package for ${module.footprintRef} (${device._attributes.package})`);
            return;
        }
        footprint = (0, board_1.readFootprint)(devicePackage, device._attributes.name, part._attributes.library, module);
    }
    const gateMap = (0, ObjMap_1.toMap)((0, Common_1.toArray)(deviceSet.gates.gate), (g) => g._attributes.name, (g) => g._attributes.symbol);
    const symbols = (0, ObjMap_1.toMap)((0, Common_1.toArray)(deviceSet.gates.gate).map((g) => getSymbol(lib, g._attributes.symbol)), (s) => s._attributes.name);
    let componentName = getComponentName(part._attributes, deviceSet, device);
    let component = project.components[componentName];
    if (component) {
        const missingSymbols = Object.values(symbols).find((s) => !component.symbols.find((existing) => existing.name === s._attributes.name));
        if (!missingSymbols && (!footprint || component.footprints.find((fp) => fp.ref === footprint.ref))) {
            return component;
        }
    }
    else {
        component = parseComponentDefinition(componentName, lib, device, deviceSet, symbols, footprint);
    }
    if (footprint && !component.footprints.find((fp) => fp.ref === footprint.ref)) {
        component.footprints.push(footprint);
        const padArr = (0, Common_1.toArray)(component.numPads);
        padArr.push(footprint.pads.length);
        component.numPads = padArr;
    }
    parseComponentSignals(component, deviceSet, symbols, gateMap, device, module, model.warnings);
    project['components'][component.name] = component;
    return component;
}
function getComponentName(objWithLib, deviceSet, device) {
    const parts = [objWithLib.library, deviceSet._attributes.name];
    if (device?._attributes.name) {
        parts.push(device._attributes.name);
    }
    if (device?._attributes.package) {
        parts.push(device._attributes.package);
    }
    return parts.join('::');
}
function parseComponentSignals(component, deviceSet, symbols, gateMap, device, module, warnings) {
    for (const gate of parseGates(deviceSet.gates.gate)) {
        const symbol = symbols[gate.symbol];
        if (!symbol) {
            warnings.push(`Could not find symbol with name ${gate.symbol} for deviceset ${deviceSet._attributes.name} gate ${gate.name}`);
        }
        component.symbols.push(loadSymbol(symbol, gate, device, warnings));
    }
    component.signals = parseGates(deviceSet.gates.gate).flatMap((gate) => {
        const symbol = symbols[gateMap[gate.name]];
        let canInferSignalType = !!symbol;
        if (!canInferSignalType) {
            warnings.push(`Could not find symbol '${gateMap[gate.name]}' for gate ${gate.name}` +
                (!!module ? ` for part ${module.footprintRef}` : ''));
        }
        return (0, Common_1.toArray)(symbol.pin)
            .map((p) => {
            let blockInfo;
            if (Object.entries(gateMap).length > 1) {
                blockInfo = {
                    index: gate.index,
                    name: gate.name.replace(/^G\$(-|_|\W)*/gi, ''),
                };
            }
            let signal;
            if (!device?.connects?.connect) {
                if (component.symbols.length === 1 && component.symbols[0].pins.length === 1) {
                    signal = {
                        id: `${gate.name}::${p._attributes.name}`,
                        name: p._attributes.name,
                        pinRefs: [`${gate.name}::${p._attributes.name}`],
                        padRefs: [],
                        blockInfo,
                    };
                }
                else {
                    return;
                }
            }
            else {
                const connects = (0, Common_1.toArray)(device?.connects?.connect).filter((c) => c._attributes.pin.split(' ').indexOf(p._attributes.name) !== -1);
                signal = {
                    id: `${gate.name}::${p._attributes.name}`,
                    name: p._attributes.name,
                    pinRefs: connects.flatMap((c) => c._attributes.pin.split(' ').map((p) => `${c._attributes.gate}::${p}`)),
                    padRefs: connects.flatMap((c) => c._attributes.pad.split(' ')),
                    blockInfo,
                };
            }
            if (canInferSignalType) {
                return inferSignalProperties(symbol, p, signal);
            }
            return signal;
        })
            .filter((s) => !!s);
    });
}
function parseComponentDefinition(componentName, lib, device, deviceSet, symbols, footprint) {
    const component = {
        name: componentName,
        signals: [],
        footprints: [],
        symbols: [],
        numPins: 0,
        numPads: 0,
    };
    (0, common_1.addMetadata)(component, 'eagle.library', lib._attributes?.name);
    (0, common_1.addMetadata)(component, 'eagle.deviceset.name', deviceSet._attributes?.name);
    (0, common_1.addMetadata)(component, 'eagle.deviceset.description', deviceSet.description?._text);
    if (device?.connects) {
        component.numPins = (0, Common_1.toArray)(device.connects?.connect).length;
    }
    else {
        component.numPins = (0, Common_1.toArray)(deviceSet.gates.gate)
            .map((gate) => {
            const symbol = symbols[gate._attributes.symbol];
            return (0, Common_1.toArray)(symbol.pin).length;
        })
            .reduce((p, c) => p + c, 0);
    }
    if (footprint) {
        component.footprints.push(footprint);
        component.numPads = component.footprints.map((f) => f.pads.length);
    }
    return component;
}
function inferSignalProperties(symbol, pin, signal) {
    if (!signal.attributes) {
        signal.attributes = {};
    }
    switch (pin._attributes.direction?.toLowerCase()) {
        case PinDirection.BidirectionalIO:
            signal.signalDirection = sigma_engine_shared_1.SignalDirection.BiDirectionalIO;
            break;
        case PinDirection.Input:
            signal.signalDirection = sigma_engine_shared_1.SignalDirection.Input;
            break;
        case PinDirection.Output:
            signal.signalDirection = sigma_engine_shared_1.SignalDirection.Output;
            break;
        case PinDirection.HiImpedence:
            setParametric(signal, SignalParametrics.HiImpedence, true);
            break;
        case PinDirection.Passive:
        case PinDirection.OpenCollectorOpenDrain:
            break;
        case PinDirection.NotConnected:
            signal.attributes.noConnect = true;
            break;
        case PinDirection.Supply:
            signal.attributes.power = true;
            signal.signalDirection = sigma_engine_shared_1.SignalDirection.Output;
            break;
        default:
        case PinDirection.Power:
            inferSignalPowerAttributes(symbol, signal, pin);
            break;
    }
    return signal;
}
function parseSchematicXml(schematic) {
    const json = (0, xml_js_1.xml2json)(schematic, { compact: true });
    return JSON.parse(json);
}
function getSymbol(lib, symbol) {
    return (0, Common_1.toArray)(lib.symbols?.symbol).find((s) => s._attributes.name === symbol);
}
function loadSymbol(symbol, gate, device, warnings) {
    const fp = {
        name: symbol._attributes.name,
        ref: `${device?._attributes.package}::${gate.name}`,
        drawings: [],
        pins: [],
        metadata: {},
    };
    (0, Common_1.toArray)(symbol.pin).forEach((pin, no) => {
        const start = [parseFloat(pin._attributes.x), -parseFloat(pin._attributes.y)];
        const length = pinLengthToNumber(pin._attributes.length);
        const rotation = (0, common_1.parseRotation)(pin._attributes.rot);
        const drawing = {
            type: 'pin',
            ref: `${gate.name}::${pin._attributes.name}`,
            name: pin._attributes.name,
            number: no.toString(),
            start,
            end: (0, Transform_1.getLineOffset)(length, rotation.rot, start),
            layer: (0, layers_1.getEagleSchLayerByType)(sigma_engine_shared_1.SchLayerType.Pin).id,
            drawings: [],
        };
        if (pin._attributes.direction?.toLowerCase() === PinDirection.Supply) {
            drawing.isNetLabel = true;
        }
        fp.pins.push(drawing);
        switch ((pin._attributes.visible || PinVisibility.Both).toLowerCase()) {
            case PinVisibility.Off:
                break;
            case PinVisibility.Both:
                drawing.drawings.push(parseSymbolPinText(pin, gate, device, drawing, rotation, 'pin'));
                if (device) {
                    drawing.drawings.push(parseSymbolPinText(pin, gate, device, drawing, rotation, 'pad'));
                }
                break;
            case PinVisibility.Pin:
                drawing.drawings.push(parseSymbolPinText(pin, gate, device, drawing, rotation, 'pin'));
                break;
            case PinVisibility.Pad:
                if (device) {
                    drawing.drawings.push(parseSymbolPinText(pin, gate, device, drawing, rotation, 'pad'));
                }
                break;
            default:
                warnings.push(`Unknown pin visibility '${pin._attributes.visible}'`);
                break;
        }
        if (drawing.drawings.length === 0) {
            delete drawing.drawings;
        }
    });
    (0, Common_1.toArray)(symbol.rectangle).forEach((rect) => {
        const parsed = (0, board_1.readRectangle)(rect);
        fp.drawings.push({
            layers: [parsed.layer],
            drawing: parsed.shape,
        });
    });
    (0, Common_1.toArray)(symbol.circle).forEach((circ) => {
        const parsed = (0, board_1.readCircle)(circ);
        fp.drawings.push({
            layers: [parsed.layer],
            drawing: parsed.shape,
        });
    });
    (0, Common_1.toArray)(symbol.wire).forEach((w) => {
        const parsed = parseWire(w);
        fp.drawings.push({
            layers: [parsed.layer],
            drawing: parsed.shape,
        });
    });
    (0, Common_1.toArray)(symbol.text).forEach((t) => {
        const parsed = (0, board_1.readText)(t, true);
        parsed.shape.rejustifyOnRotate = true;
        fp.drawings.push({
            layers: [parsed.layer],
            drawing: parsed.shape,
        });
    });
    (0, Common_1.toArray)(symbol.polygon).forEach((p) => {
        const parsed = (0, board_1.readPolygon)(p);
        fp.drawings.push({
            layers: [parseInt(p._attributes.layer)],
            drawing: parsed,
        });
    });
    return fp;
}
function parseSymbolPinText(pin, gate, device, parsedPin, rotation, type) {
    const layer = (0, layers_1.getEagleSchLayerByType)(sigma_engine_shared_1.SchLayerType.PinName);
    const drawing = {
        type: 'text',
        value: '',
        pos: parsedPin.end,
        angle: (0, Transform_1.clampRotation)(rotation.rot),
        mirror: rotation.mirrored,
        attr: [],
        justify: [-1, 0],
        size: 1.778,
        thickness: 1,
        rejustifyOnRotate: true,
    };
    switch (type) {
        case 'pin':
            drawing.value = pin._attributes.name;
            drawing.pos = (0, Transform_1.getLineOffset)(2.54, rotation.rot, parsedPin.end);
            break;
        case 'pad':
            const connect = (0, Common_1.toArray)(device?.connects?.connect).find((c) => c._attributes.gate === gate.name && c._attributes.pin === pin._attributes.name);
            if (connect) {
                drawing.pos = (0, Transform_1.getLineOffset)(-0.5, -rotation.rot, parsedPin.end);
                drawing.value = parsePadSymbolValue(connect._attributes.pad);
                drawing.justify = [-drawing.justify[0], -1];
            }
            break;
    }
    if (drawing.angle >= 90 && drawing.angle < 180) {
        drawing.justify[0] *= -1;
    }
    if (drawing.angle >= 180 && drawing.angle < 270) {
        drawing.justify[1] *= -1;
    }
    if (drawing.angle >= 270) {
        drawing.justify[0] *= -1;
        drawing.justify[1] *= -1;
    }
    return {
        layers: [layer.id],
        drawing,
    };
}
function parsePadSymbolValue(pad) {
    const parts = pad.split(/\s/);
    if (parts.length < 2) {
        return pad;
    }
    return `${parts[0]}*${parts.length}`;
}
function setParametric(signal, parameter, value) {
    if (!signal.parametrics) {
        signal.parametrics = {};
    }
    signal.parametrics[parameter] = value;
}
function inferSignalPowerAttributes(symbol, signal, pin) {
    const matches = new Map();
    matches.set('ground', [
        { match: 'gnd', dist: 1 },
        { match: 'agnd', dist: 0 },
    ]);
    matches.set('power', [
        { match: 'vcc', dist: 1 },
        { match: 'vin', dist: 1 },
        { match: 'vdd', dist: 1 },
        { match: 'vdc', dist: 1 },
        { match: 'avcc', dist: 0 },
        { match: 'avdd', dist: 0 },
    ]);
    const pinName = pin._attributes.name?.toLowerCase();
    for (const sigType of matches.keys()) {
        for (const match of matches.get(sigType)) {
            if (strDist.get(match.match, pinName) <= match.dist) {
                if (!signal.attributes) {
                    signal.attributes = {};
                }
                if (sigType === 'ground') {
                    signal.attributes.ground = true;
                }
                else if (sigType === 'power') {
                    signal.attributes.power = true;
                }
                return;
            }
        }
    }
}
function pinLengthToNumber(length) {
    const baseLen = 2.54;
    switch (length) {
        case PinLength.Middle:
            return 2 * baseLen;
        case PinLength.Short:
            return baseLen;
        case PinLength.Point:
            return 0;
        default:
        case PinLength.Long:
            return 3 * baseLen;
    }
}
function locateModuleProperty(module, property) {
    return {
        ...property,
        isPositionOffset: true,
    };
}
function calculateJustifyAndMirrorForLabels(rot) {
    const isUpsideDown = rot >= 90 && rot <= 180;
    const mirror = isUpsideDown ? [true, true] : [false, false];
    const justify = isUpsideDown ? [1, -1] : [-1, 1];
    return { justify, mirror };
}
function parseGates(gate) {
    return (0, Common_1.toArray)(gate).map((g, index) => {
        return {
            name: g._attributes.name,
            symbol: g._attributes.symbol,
            x: g._attributes.x,
            y: g._attributes.y,
            index,
        };
    });
}
//# sourceMappingURL=schematic.js.map